import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { MhProduct } from '@/types/mh';
import ServiceCard from '@/pages/services/components/ServiceCard';
import {
  groupProductsByCategory,
  mhProductToCard,
} from '@/utils/product-category';

type HotServicesProps = {
  products: MhProduct[];
  loading: boolean;
  error: string;
};

type CategoryNavItem = {
  key: string;
  title: string;
  count: number;
};

function CategoryNavButton({
  item,
  active,
  onClick,
  variant,
}: {
  item: CategoryNavItem;
  active: boolean;
  onClick: () => void;
  variant: 'sidebar' | 'pill';
}) {
  if (variant === 'sidebar') {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer text-left ${
          active ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
        }`}
      >
        <span className="truncate">{item.title}</span>
        <span className={`text-xs flex-shrink-0 ${active ? 'text-white/70' : 'text-gray-400'}`}>
          {item.count}
        </span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
        active ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      }`}
    >
      {item.title}
      <span className={`ml-1.5 ${active ? 'text-white/70' : 'text-gray-400'}`}>({item.count})</span>
    </button>
  );
}

export default function HotServices({ products, loading, error }: HotServicesProps) {
  const [activeKey, setActiveKey] = useState('all');

  const categoryGroups = useMemo(() => groupProductsByCategory(products), [products]);

  const navItems = useMemo((): CategoryNavItem[] => {
    const items: CategoryNavItem[] = [
      { key: 'all', title: '全部', count: products.length },
    ];
    for (const g of categoryGroups) {
      items.push({ key: g.key, title: g.title, count: g.products.length });
    }
    return items;
  }, [categoryGroups, products.length]);

  const allCategorySections = useMemo(() => {
    return categoryGroups.map((g) => ({
      key: g.key,
      title: g.title,
      items: g.products.map(mhProductToCard),
    }));
  }, [categoryGroups]);

  const displayedProducts = useMemo(() => {
    const group = categoryGroups.find((g) => g.key === activeKey);
    return group?.products ?? [];
  }, [activeKey, categoryGroups]);

  const displayedCards = useMemo(
    () => displayedProducts.map(mhProductToCard),
    [displayedProducts],
  );

  const activeTitle = navItems.find((n) => n.key === activeKey)?.title ?? '全部';
  const visibleCount = activeKey === 'all' ? products.length : displayedCards.length;

  return (
    <section id="hot-services" className="py-10 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-5">
          <div>
            <p className="text-gray-400 text-sm font-semibold mb-1 uppercase tracking-wider">Services</p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">人工查询服务</h2>
          </div>
          <Link
            to="/services"
            className="text-sm text-gray-600 hover:text-gray-900 font-medium flex items-center gap-1 cursor-pointer whitespace-nowrap"
          >
            查看全部 <i className="ri-arrow-right-line" />
          </Link>
        </div>

        {error && (
          <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            {error}
          </div>
        )}

        {loading ? (
          <div className="py-16 text-center text-gray-500 text-sm">加载产品中…</div>
        ) : products.length === 0 ? (
          <div className="py-16 text-center rounded-2xl border border-dashed border-gray-200 bg-gray-50">
            <p className="text-gray-600 mb-2">暂无上架产品</p>
            <p className="text-gray-400 text-sm mb-4">请在后台「C端产品」中上架后刷新页面</p>
            <Link
              to="/services"
              className="inline-flex items-center gap-1 text-sm font-medium text-gray-900 underline"
            >
              前往服务列表
            </Link>
          </div>
        ) : (
          <>
            {/* Mobile: horizontal category pills */}
            <div className="lg:hidden mb-5 -mx-1">
              <div className="flex gap-2 overflow-x-auto pb-1 px-1 scrollbar-thin">
                {navItems.map((item) => (
                  <CategoryNavButton
                    key={item.key}
                    item={item}
                    active={activeKey === item.key}
                    onClick={() => setActiveKey(item.key)}
                    variant="pill"
                  />
                ))}
              </div>
            </div>

            <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
              {/* Desktop: left category sidebar */}
              <aside className="hidden lg:block w-56 shrink-0">
                <nav className="sticky top-24 space-y-1 rounded-2xl border border-gray-100 bg-gray-50/80 p-2">
                  {navItems.map((item) => (
                    <CategoryNavButton
                      key={item.key}
                      item={item}
                      active={activeKey === item.key}
                      onClick={() => setActiveKey(item.key)}
                      variant="sidebar"
                    />
                  ))}
                </nav>
              </aside>

              {/* Right: product grid */}
              <div className="flex-1 min-w-0">
                <p className="hidden lg:block text-sm text-gray-500 mb-4">
                  当前分类：<span className="font-semibold text-gray-900">{activeTitle}</span>
                  <span className="text-gray-400 ml-2">共 {visibleCount} 个服务</span>
                </p>

                {activeKey === 'all' ? (
                  <div className="space-y-5">
                    {allCategorySections.map((section) => (
                      <section
                        key={section.key}
                        className="rounded-2xl border border-gray-100 bg-gray-50/70 p-3 sm:p-4"
                      >
                        <div className="mb-3 flex items-center justify-between gap-3 border-b border-gray-100 pb-3">
                          <div className="flex min-w-0 items-center gap-2">
                            <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl bg-white text-gray-500 ring-1 ring-gray-100">
                              <i className="ri-price-tag-3-line" />
                            </span>
                            <h3 className="truncate text-base font-bold text-gray-900">
                              {section.title}
                            </h3>
                          </div>
                          <span className="flex-shrink-0 text-sm text-gray-400">
                            {section.items.length} 个服务
                          </span>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                          {section.items.map((service) => (
                            <ServiceCard key={service.slug} service={service} />
                          ))}
                        </div>
                      </section>
                    ))}
                  </div>
                ) : displayedCards.length === 0 ? (
                  <div className="py-12 text-center rounded-2xl border border-dashed border-gray-200 bg-gray-50">
                    <p className="text-gray-500 text-sm">该分类下暂无产品</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {displayedCards.map((service) => (
                      <ServiceCard key={service.slug} service={service} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
